'use client';

// Trial + upgrade UI for the standalone SaaS shell.
// - TrialBanner: slim strip shown while on a free trial.
// - UpgradeModal: the plan + "Pay now" request (real online payment via
//   Aumonline's gateway is not wired yet; this records the request).
// - TrialExpired: full-screen soft-lock once the trial runs out.

import React, { useEffect, useState } from "react";
import { Sparkles, Check, X, LogOut, CalendarClock, ArrowUpRight, BadgeCheck } from "lucide-react";
import { api, errText } from "./api";

const FONT = { fontFamily: "'Hind', 'IBM Plex Sans', system-ui, sans-serif" };

// LOCKED prices (19 Jul 2026). No GST, no add-ons — the price is final.
const PLANS = {
  solo: {
    key: "solo", name: "Solo", monthly: 495, yearly: 4950,
    tagline: "For a one-person business",
    features: [
      "Unlimited enquiries",
      "Pipeline, call-back & WhatsApp follow-ups",
      "Owner's business view & insights",
      "Works on phone + desktop",
    ],
  },
  team: {
    key: "team", name: "Team", monthly: 975, yearly: 9750, popular: true,
    tagline: "For a business with a team",
    features: [
      "Everything in Solo, plus —",
      "Your whole team, with roles & invites",
      "Customer lists — companies & individuals",
      "One-click Excel import of your data",
    ],
  },
};

// Manual payment details shown after "Pay now" until Razorpay is live.
// PLACEHOLDERS — replace with the owner's real UPI ID / QR and bank details.
const PAYMENT_DETAILS = {
  upiId: "UPI ID to be added",
  bank: {
    name: "Account details to be added",
    account: "—",
    ifsc: "—",
  },
  helpline: "Reply on WhatsApp with your payment screenshot and we'll activate you within a few hours.",
};

const fmtDay = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
  } catch { return iso; }
};

// Canonical paid-plan catalogue + which moves count as an upgrade.
const PLAN_LABELS = {
  "solo-monthly": { name: "Solo · monthly", amount: 495, per: "₹495/month" },
  "solo-yearly": { name: "Solo · yearly", amount: 4950, per: "₹4,950/year" },
  "team-monthly": { name: "Team · monthly", amount: 975, per: "₹975/month" },
  "team-yearly": { name: "Team · yearly", amount: 9750, per: "₹9,750/year" },
};
const UPGRADE_PATHS = {
  "solo-monthly": ["solo-yearly", "team-monthly", "team-yearly"],
  "solo-yearly": ["team-yearly"],
  "team-monthly": ["team-yearly"],
  "team-yearly": [],
};
const UPGRADE_BLURBS = {
  "solo-yearly": "Pay once a year and save ₹990 — 2 months free.",
  "team-monthly": "Adds team logins, team pulse, customer lists & Excel import.",
  "team-yearly": "Team features, paid yearly — 2 months free.",
};

// Live pricing — /billing/pricing is public and carries the founding-price
// ticker (first 10 paying Team businesses stay at ₹975 for life; then ₹1,495).
export const usePricing = () => {
  const [pricing, setPricing] = useState(null);
  useEffect(() => {
    api.get("/billing/pricing").then((r) => setPricing(r.data)).catch(() => {});
  }, []);
  return pricing;
};

const loadRzpScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

// Opens the real Razorpay checkout for a plan. Resolves with:
//   {paid, billing}  — money received & plan activated
//   {fallback: true} — gateway not configured (preview) → manual UPI flow
//   {cancelled: true} — user closed the payment window
export const payWithRazorpay = async (planKey) => {
  let order;
  try {
    order = (await api.post("/billing/razorpay/order", { plan: planKey })).data;
  } catch (err) {
    if (err?.response?.status === 503) return { fallback: true };
    throw err;
  }
  const ok = await loadRzpScript();
  if (!ok) return { fallback: true };
  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: "Jigyasa",
      description: order.description,
      order_id: order.order_id,
      prefill: order.prefill,
      theme: { color: "#e11d48" },
      modal: { ondismiss: () => resolve({ cancelled: true }) },
      handler: async (res) => {
        try {
          const v = await api.post("/billing/razorpay/verify", res);
          resolve({ paid: true, billing: v.data.billing });
        } catch (err) {
          reject(err);
        }
      },
    });
    rzp.open();
  });
};

// Shared "how to pay us" block — real UPI QR / bank details from the owner's
// God Panel settings, with tap-to-enlarge. Used by SubscribeBox and My plan.
export const PaymentInstructions = () => {
  const [payOpts, setPayOpts] = useState(null);
  const [qrZoom, setQrZoom] = useState(false);

  useEffect(() => {
    api.get("/billing/payment-options").then((r) => setPayOpts(r.data)).catch(() => {});
  }, []);

  const qrSrc = payOpts?.upi_qr || "";
  const hasBank = !!(payOpts?.bank_name || payOpts?.account_number);
  return (
    <>
      <div data-testid="payment-instructions" className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-zinc-200 bg-white p-4">
          <div className="mb-1 text-sm font-semibold text-zinc-900">Pay by UPI</div>
          <p data-testid="payment-upi-id" className="text-sm text-zinc-600">
            {payOpts?.upi_id || (qrSrc ? "Scan the QR code below to pay" : PAYMENT_DETAILS.upiId)}
          </p>
          {qrSrc && (
            <>
              <button
                type="button"
                data-testid="payment-upi-qr-btn"
                onClick={() => setQrZoom(true)}
                className="mt-2 block"
              >
                <img
                  data-testid="payment-upi-qr"
                  src={qrSrc}
                  alt="UPI QR code — tap to enlarge"
                  className="w-full max-w-[280px] rounded-lg border border-zinc-200"
                />
              </button>
              <p className="mt-1.5 text-xs text-zinc-500">Tap the QR code to make it bigger.</p>
            </>
          )}
        </div>
        <div className="rounded-xl border border-zinc-200 bg-white p-4">
          <div className="mb-1 text-sm font-semibold text-zinc-900">Pay by bank transfer</div>
          {hasBank ? (
            <p data-testid="payment-bank-details" className="text-sm text-zinc-600">
              {payOpts.bank_name || "—"}
              {payOpts.account_name ? <><br />{payOpts.account_name}</> : null}
              <br />A/c: {payOpts.account_number || "—"} · IFSC: {payOpts.ifsc || "—"}
            </p>
          ) : (
            <p data-testid="payment-bank-details" className="text-sm text-zinc-600">
              {PAYMENT_DETAILS.bank.name}
              <br />A/c: {PAYMENT_DETAILS.bank.account} · IFSC: {PAYMENT_DETAILS.bank.ifsc}
            </p>
          )}
        </div>
      </div>
      <p className="text-xs text-zinc-500">{payOpts?.instructions || PAYMENT_DETAILS.helpline}</p>
      {qrZoom && (
        <div
          data-testid="payment-qr-zoom"
          className="fixed inset-0 z-[70] flex cursor-pointer items-center justify-center bg-black/85 p-4"
          onClick={() => setQrZoom(false)}
        >
          <div className="flex max-h-full flex-col items-center">
            <img src={qrSrc} alt="UPI QR code" className="max-h-[80vh] max-w-full rounded-xl bg-white p-2" />
            <p className="mt-3 text-sm text-white/80">Tap anywhere to close</p>
          </div>
        </div>
      )}
    </>
  );
};

export const TrialBanner = ({ billing, onUpgrade }) => {
  if (!billing || billing.status !== "trial") return null;
  const days = billing.days_left ?? 0;
  return (
    <div
      data-testid="trial-banner"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-900"
      style={FONT}
    >
      <Sparkles size={16} className="text-rose-600" />
      <span data-testid="trial-days-left" className="font-medium">
        {days === 1 ? "Last day of your free trial" : `${days} days left in your free trial`}
      </span>
      <button
        data-testid="trial-banner-upgrade-btn"
        onClick={onUpgrade}
        className="ml-auto rounded-full bg-zinc-900 px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-zinc-700"
      >
        Upgrade
      </button>
    </div>
  );
};

const PlanOption = ({ plan, yearly, selected, onSelect, founding = null, standard = null }) => {
  const isFoundingTeam = plan.key === "team" && founding?.active;
  const strike = isFoundingTeam && standard ? (yearly ? Math.floor(standard.yearly / 12) : standard.monthly) : null;
  return (
  <button
    type="button"
    data-testid={`plan-option-${plan.key}`}
    onClick={() => onSelect(plan.key)}
    className={`relative rounded-2xl border-2 p-4 text-left transition-colors ${
      selected ? "border-rose-600 bg-rose-50/40 ring-2 ring-rose-500/20" : "border-zinc-200 bg-white hover:border-zinc-300"
    }`}
  >
    {isFoundingTeam ? (
      <span data-testid="plan-founding-badge" className="absolute -top-2.5 right-3 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
        Founding price · {founding.left} of {founding.total} left
      </span>
    ) : plan.popular && (
      <span className="absolute -top-2.5 right-3 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
        Most popular
      </span>
    )}
    <div className="text-sm font-semibold text-zinc-900">{plan.name}</div>
    <div className="text-[11px] text-zinc-500">{plan.tagline}</div>
    <div className="mt-2 flex items-baseline gap-1">
      {strike && <span className="mr-0.5 text-sm text-zinc-400 line-through">₹{strike}</span>}
      <span className="text-2xl font-semibold tracking-tight text-zinc-900">
        ₹{yearly ? Math.floor(plan.yearly / 12) : plan.monthly}
      </span>
      <span className="text-xs text-zinc-500">/mo</span>
    </div>
    <div className="text-[11px] text-zinc-500">
      {yearly ? `₹${plan.yearly.toLocaleString("en-IN")} billed yearly · 2 months free` : "Billed monthly · no extras, ever"}
    </div>
    {isFoundingTeam && (
      <div className="mt-1 text-[11px] font-medium text-amber-700">Locked for life — goes to ₹{standard ? standard.monthly.toLocaleString("en-IN") : "1,495"}/mo after 10 businesses.</div>
    )}
    <ul className="mt-3 space-y-1.5 text-xs">
      {plan.features.map((f) => (
        <li key={f} className="flex items-start gap-1.5 text-zinc-700">
          <Check size={13} className="mt-0.5 shrink-0 text-rose-600" />
          <span>{f}</span>
        </li>
      ))}
    </ul>
  </button>
  );
};

// Shared subscribe action — pick a plan, then pay. Until the Razorpay
// gateway is live, "Pay now" records the request and shows the owner's
// UPI / bank details with clear "we activate you" instructions.
export const SubscribeBox = ({ onSubscribed, intendedPlan = null }) => {
  const intended = /^(solo|team)-(monthly|yearly)$/.test(intendedPlan || "") ? intendedPlan.split("-") : null;
  const pricing = usePricing();
  const [selected, setSelected] = useState(intended ? intended[0] : "team");
  const [cycle, setCycle] = useState(intended ? intended[1] : "yearly");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [paidInfo, setPaidInfo] = useState(null);

  const planMap = {
    solo: PLANS.solo,
    team: {
      ...PLANS.team,
      monthly: pricing?.team?.monthly ?? PLANS.team.monthly,
      yearly: pricing?.team?.yearly ?? PLANS.team.yearly,
    },
  };

  const subscribe = async () => {
    setError("");
    setBusy(true);
    try {
      const planKey = `${selected}-${cycle}`;
      const res = await payWithRazorpay(planKey);
      if (res.paid) {
        setPaidInfo(res.billing || {});
        onSubscribed?.();
        return;
      }
      if (res.cancelled) return;
      // Gateway off (preview / keys pending) — manual UPI/bank fallback.
      await api.post("/billing/subscribe", { plan: planKey });
      setDone(true);
      onSubscribed?.();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const yearlyNow = cycle === "yearly";
  const planNow = planMap[selected];
  const amount = yearlyNow ? planNow.yearly : planNow.monthly;

  if (paidInfo) {
    return (
      <div data-testid="subscribe-paid-success" className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
        <div className="mb-1 flex items-center gap-2 font-semibold"><BadgeCheck size={16} /> Payment received — you're on!</div>
        Your <strong>{planNow.name} · {yearlyNow ? "yearly" : "monthly"}</strong> plan is active
        {paidInfo.paid_until ? <> until <strong>{fmtDay(paidInfo.paid_until)}</strong></> : null}. Welcome aboard.
      </div>
    );
  }

  if (done) {
    return (
      <div data-testid="subscribe-success" className="space-y-3">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          Your <strong>{planNow.name} · {yearlyNow ? "yearly" : "monthly"}</strong> plan is reserved.
          Pay <strong>₹{amount.toLocaleString("en-IN")}</strong> using either option below and we'll switch you on.
        </div>
        <PaymentInstructions />
      </div>
    );
  }

  const yearly = yearlyNow;
  const plan = planNow;
  const priceLabel = yearly ? `₹${plan.yearly.toLocaleString("en-IN")}/yr` : `₹${plan.monthly}/mo`;
  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500">The price you see is the final price — no add-ons, nothing extra at checkout, cancel anytime.</p>
      <div className="flex justify-center">
        <div className="inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 p-1" data-testid="subscribe-cycle-toggle">
          <button
            type="button"
            data-testid="subscribe-cycle-monthly"
            onClick={() => setCycle("monthly")}
            className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${!yearly ? "bg-zinc-900 text-white" : "text-zinc-600"}`}
          >
            Monthly
          </button>
          <button
            type="button"
            data-testid="subscribe-cycle-yearly"
            onClick={() => setCycle("yearly")}
            className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${yearly ? "bg-zinc-900 text-white" : "text-zinc-600"}`}
          >
            Yearly · 2 months free
          </button>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <PlanOption plan={planMap.solo} yearly={yearly} selected={selected === "solo"} onSelect={setSelected} />
        <PlanOption plan={planMap.team} yearly={yearly} selected={selected === "team"} onSelect={setSelected} founding={pricing?.founding} standard={pricing?.team_standard} />
      </div>
      {error && (
        <div data-testid="subscribe-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}
      <button
        data-testid="subscribe-now-btn"
        onClick={subscribe}
        disabled={busy}
        className="w-full rounded-full bg-rose-600 py-3 font-medium text-white transition-colors hover:bg-rose-700 disabled:opacity-50"
      >
        {busy ? "Please wait…" : `Pay now & subscribe — ${plan.name} (${priceLabel})`}
      </button>
      <p className="text-center text-xs text-zinc-400">
        {pricing?.payments_live
          ? "Pay securely by UPI, card or netbanking — you're activated instantly."
          : "Pay by UPI or bank transfer — we activate you the same day. Online card payment is coming soon."}
      </p>
    </div>
  );
};

export const UpgradeModal = ({ open, onClose, onSubscribed }) => {
  if (!open) return null;
  return (
    <div
      data-testid="upgrade-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-8"
      style={FONT}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold tracking-tight text-zinc-900">Choose your plan</h2>
          <button data-testid="upgrade-modal-close" onClick={onClose} className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100">
            <X size={18} />
          </button>
        </div>
        <SubscribeBox onSubscribed={onSubscribed} />
      </div>
    </div>
  );
};

export const TrialExpired = ({ onLogout, billing = null }) => {
  const paidLapsed = billing?.status === "payment_due";
  return (
    <div
      data-testid="trial-expired-screen"
      className="flex min-h-screen items-center justify-center bg-zinc-950 px-4 py-10"
      style={FONT}
    >
      <div className="w-full max-w-2xl rounded-2xl bg-white p-7 shadow-2xl">
        <div className="mb-1 text-sm font-medium uppercase tracking-[0.2em] text-rose-600">
          {paidLapsed ? "Plan expired" : "Free trial ended"}
        </div>
        <h1 className="mb-2 text-2xl font-semibold tracking-tight text-zinc-900">
          {paidLapsed ? "Your plan has expired" : "Your free trial has ended"}
        </h1>
        <p className="mb-5 text-sm text-zinc-600">
          {paidLapsed
            ? "Your data is safe and waiting. Renew — even one month — and everything is back instantly."
            : "Your data is safe and waiting. Pick a plan to pick up right where you left off."}
        </p>
        <SubscribeBox intendedPlan={billing?.plan || billing?.intended_plan} />
        <button
          data-testid="trial-expired-logout"
          onClick={onLogout}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-zinc-300 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
        >
          <LogOut size={15} /> Log out
        </button>
      </div>
    </div>
  );
};

// ── "My plan" sheet — a paying customer's view of what they're on, when it
// ends, and the upgrades that make sense for them (upgrades only; anyone
// wanting to step down talks to us at renewal). Opened from Settings → My plan
// and from the renewal banner.
export const MyPlanSheet = ({ open, onClose, billing }) => {
  const pricing = usePricing();
  const [paying, setPaying] = useState(null); // plan key mid-payment (manual)
  const [paidInfo, setPaidInfo] = useState(null); // razorpay success
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  if (!open) return null;
  const labels = { ...PLAN_LABELS };
  if (pricing?.team) {
    labels["team-monthly"] = { name: "Team · monthly", amount: pricing.team.monthly, per: `₹${pricing.team.monthly.toLocaleString("en-IN")}/month` };
    labels["team-yearly"] = { name: "Team · yearly", amount: pricing.team.yearly, per: `₹${pricing.team.yearly.toLocaleString("en-IN")}/year` };
  }
  const planKey = billing?.plan;
  const cur = labels[planKey];
  const upgrades = (UPGRADE_PATHS[planKey] || []).map((k) => ({ key: k, ...labels[k] }));

  const startPay = async (key) => {
    setBusy(key);
    setError("");
    try {
      const res = await payWithRazorpay(key);
      if (res.paid) {
        setPaidInfo({ key, billing: res.billing || {} });
      } else if (res.fallback) {
        await api.post("/billing/subscribe", { plan: key });
        setPaying(key);
      }
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy("");
    }
  };

  const payingPlan = paying ? labels[paying] : null;

  return (
    <div
      data-testid="my-plan-sheet"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-8"
      style={FONT}
      onClick={onClose}
    >
      <div
        className="max-h-full w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold tracking-tight text-zinc-900">My plan</h2>
          <button data-testid="my-plan-close" onClick={onClose} className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100">
            <X size={18} />
          </button>
        </div>

        {paidInfo ? (
          <div data-testid="my-plan-paid-success" className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
            <div className="mb-1 flex items-center gap-2 font-semibold"><BadgeCheck size={16} /> Payment received!</div>
            Your <strong>{labels[paidInfo.key]?.name}</strong> plan is active
            {paidInfo.billing?.paid_until ? <> until <strong>{fmtDay(paidInfo.billing.paid_until)}</strong></> : null}.
          </div>
        ) : payingPlan ? (
          <div className="space-y-3">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              Your <strong>{payingPlan.name}</strong> {paying === planKey ? "renewal" : "upgrade"} is reserved.
              Pay <strong>₹{payingPlan.amount.toLocaleString("en-IN")}</strong> using either option below and we'll switch you over.
            </div>
            <PaymentInstructions />
            <button
              data-testid="my-plan-back"
              onClick={() => setPaying(null)}
              className="text-sm text-zinc-500 underline hover:text-zinc-700"
            >
              ← Back to my plan
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div data-testid="my-plan-current" className="rounded-xl border border-zinc-200 bg-zinc-50 p-4">
              <div className="text-xs uppercase tracking-wider text-zinc-500">You're on</div>
              <div className="mt-0.5 text-lg font-semibold text-zinc-900">{cur ? cur.name : "—"}</div>
              <div className="text-sm text-zinc-600">{cur ? cur.per : ""}</div>
              {billing?.paid_until && (
                <div data-testid="my-plan-valid-until" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-sm text-zinc-700 ring-1 ring-zinc-200">
                  <CalendarClock size={14} className="text-rose-600" />
                  Valid until <strong>{fmtDay(billing.paid_until)}</strong>
                </div>
              )}
              {billing?.paid_until && (
                <button
                  data-testid="my-plan-renew-btn"
                  onClick={() => startPay(planKey)}
                  disabled={busy === planKey}
                  className="mt-3 block w-full rounded-full bg-rose-600 py-2.5 text-sm font-medium text-white transition-colors hover:bg-rose-700 disabled:opacity-50 sm:w-auto sm:px-6"
                >
                  {busy === planKey ? "Please wait…" : `Renew ${cur ? cur.name : ""} — ₹${cur ? cur.amount.toLocaleString("en-IN") : ""}`}
                </button>
              )}
              <p className="mt-2 text-xs text-zinc-500">Renewing early never loses days — your new period starts when the current one ends.</p>
            </div>

            {upgrades.length > 0 ? (
              <div>
                <div className="mb-2 text-sm font-semibold text-zinc-900">Move up</div>
                <div className="space-y-2">
                  {upgrades.map((u) => (
                    <button
                      key={u.key}
                      data-testid={`my-plan-upgrade-${u.key}`}
                      onClick={() => startPay(u.key)}
                      disabled={busy === u.key}
                      className="flex w-full items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-white p-4 text-left transition-colors hover:border-rose-300 hover:bg-rose-50/40 disabled:opacity-50"
                    >
                      <div>
                        <div className="text-sm font-semibold text-zinc-900">{u.name} — {u.per}</div>
                        <div className="text-xs text-zinc-500">{UPGRADE_BLURBS[u.key]}</div>
                      </div>
                      <ArrowUpRight size={18} className="shrink-0 text-rose-600" />
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div data-testid="my-plan-best" className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                <BadgeCheck size={16} /> You're on our best plan — nothing more to buy.
              </div>
            )}

            {error && (
              <div data-testid="my-plan-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {error}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Renewal reminder — a gentle strip shown to paying customers in the last
// 7 days of their paid period. The highest-value banner in the product.
export const RenewalBanner = ({ billing, onRenew }) => {
  if (!billing || billing.status !== "active" || !billing.paid_until) return null;
  let days;
  try {
    days = Math.ceil((new Date(billing.paid_until) - Date.now()) / 86400000);
  } catch { return null; }
  if (days > 7 || days < 0) return null;
  return (
    <div
      data-testid="renewal-banner"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900"
      style={FONT}
    >
      <CalendarClock size={16} className="text-amber-600" />
      <span data-testid="renewal-banner-text" className="font-medium">
        {days === 0 ? "Your plan ends today" : `Your plan ends on ${fmtDay(billing.paid_until)}`} — renew to keep going without a break.
      </span>
      <button
        data-testid="renewal-banner-renew-btn"
        onClick={onRenew}
        className="ml-auto rounded-full bg-zinc-900 px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-zinc-700"
      >
        Renew
      </button>
    </div>
  );
};
